/**
 * Reporting-period formatting and license-derived filter presets for the
 * CPE Reporting page.
 */

/** Formats a YYYY-MM-DD date as e.g. "Jan 1" or "Jan 1, 2026". */
export function formatPeriodDate(iso: string, withYear: boolean): string {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(withYear ? { year: 'numeric' } : {}),
  });
}

/** Formats a date range, omitting the start year when both ends share it. */
export function formatPeriodRange(startIso: string, endIso: string): string {
  const sameYear = startIso.slice(0, 4) === endIso.slice(0, 4);
  return `${formatPeriodDate(startIso, !sameYear)} – ${formatPeriodDate(endIso, true)}`;
}

/** Minimal structural slice of LicenseFulfillmentDto needed to build presets. */
export interface LicensePresetSource {
  licenseId: string;
  stateCode: string;
  stateName?: string | null;
  /** ISO datetime or YYYY-MM-DD */
  periodStart: string;
  periodEnd: string;
}

export interface LicensePreset {
  /** Toggle value, namespaced to avoid colliding with calendar preset keys. */
  key: string;
  /** State display name (falls back to state code) — used in the summary overline. */
  stateLabel: string;
  /** Button text; includes the period range only when the same state appears twice. */
  buttonLabel: string;
  startDate: string; // YYYY-MM-DD
  endDate: string;   // YYYY-MM-DD
}

/**
 * Maps set-up licenses to reporting-period presets, preserving API order
 * (primary first, then state code).
 *
 * - Licenses with an identical (stateCode, periodStart, periodEnd) collapse
 *   into one preset — duplicate buttons doing the same thing are noise.
 * - The same state with *different* periods (possible: e.g. TN windows depend
 *   on license-number parity) keeps both presets, disambiguated by appending
 *   the period range to the label.
 */
export function buildLicensePresets(licenses: readonly LicensePresetSource[]): LicensePreset[] {
  const seen = new Set<string>();
  const deduped: Array<{ license: LicensePresetSource; startDate: string; endDate: string }> = [];

  for (const license of licenses) {
    const startDate = license.periodStart.slice(0, 10);
    const endDate = license.periodEnd.slice(0, 10);
    const dedupeKey = `${license.stateCode}|${startDate}|${endDate}`;
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);
    deduped.push({ license, startDate, endDate });
  }

  const presetsPerState = new Map<string, number>();
  for (const { license } of deduped) {
    presetsPerState.set(license.stateCode, (presetsPerState.get(license.stateCode) ?? 0) + 1);
  }

  return deduped.map(({ license, startDate, endDate }) => {
    const stateLabel = license.stateName?.trim() || license.stateCode;
    const collides = (presetsPerState.get(license.stateCode) ?? 0) > 1;
    return {
      key: `license:${license.licenseId}`,
      stateLabel,
      buttonLabel: collides ? `${stateLabel} · ${formatPeriodRange(startDate, endDate)}` : stateLabel,
      startDate,
      endDate,
    };
  });
}
